/* Dartec theme — the only site-wide script.
 *
 * The room plan carries its own behaviour inside its section, because a
 * section can be re-rendered on its own by the theme editor and its script
 * has to come back with it. What is left here is the header drawer and the
 * light/dark switch, both of which belong to the layout and outlive every
 * section, and the collection sort, which listens on the document so a
 * re-rendered section needs nothing re-bound.
 *
 * The switch is split in two on purpose: APPLYING a stored theme is inline in
 * theme.liquid before the stylesheet, because a deferred script cannot stop a
 * cream frame reaching a dark-preferring visitor. Only the pressing is here.
 *
 * No framework and no build step. There are four behaviours on this store;
 * a bundler would be more machinery than the thing it bundles. */
(function () {
  'use strict';

  function drawer() {
    var burger = document.querySelector('[data-dt-burger]');
    var panel = document.querySelector('[data-dt-drawer]');
    if (!burger || !panel) return;

    var nav = panel.querySelector('.dt-drawer__nav');
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    var opening = null;

    function set(open, animate) {
      if (opening) { opening.cancel(); opening = null; }
      burger.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel.hidden = !open;
      document.documentElement.classList.toggle('dt-locked', open);
      // Opening, the links fade down 6px into place, so the menu reads as
      // coming out of the bar above it rather than appearing under it.
      // Closing is instant: the visitor has already moved on.
      if (open && animate && nav && nav.animate) {
        var ease = getComputedStyle(nav).getPropertyValue('--dt-ease-out').trim() || 'ease-out';
        opening = nav.animate(
          reduce.matches
            ? [{ opacity: 0 }, { opacity: 1 }]
            : [{ opacity: 0, transform: 'translateY(-6px)' }, { opacity: 1, transform: 'none' }],
          { duration: reduce.matches ? 160 : 200, easing: ease }
        );
      }
    }

    // e.detail is 0 when Enter or Space pressed the button. A keyboard
    // opening gets no motion, the same rule as the room tabs.
    burger.addEventListener('click', function (e) {
      set(panel.hidden, e.detail !== 0);
    });

    // Escape closes, and focus goes back to the control that opened it —
    // otherwise a keyboard user is left at the top of the document.
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !panel.hidden) {
        set(false);
        burger.focus();
      }
    });

    // A resize past the breakpoint leaves the drawer open behind a layout
    // that no longer shows the control to close it.
    var wide = window.matchMedia('(min-width: 750px)');
    var onChange = function (e) { if (e.matches) set(false); };
    if (wide.addEventListener) wide.addEventListener('change', onChange);
    else if (wide.addListener) wide.addListener(onChange);
  }

  /* The light/dark switch.
   *
   * The *applying* of a stored choice happens inline in theme.liquid, before
   * the stylesheet, so there is no flash. This half only handles pressing it,
   * which by definition happens after the page is up.
   *
   * Three states, two of which are reachable here. No attribute means
   * 'system', and the CSS media query decides -- that is the default and it
   * keeps tracking the OS. Pressing commits to an explicit light or dark,
   * which then survives the OS changing underneath it. That is the point of
   * a switch: a choice you made outranks a choice your laptop made at
   * sunset. */
  function theme() {
    var button = document.querySelector('[data-dt-theme-toggle]');
    if (!button) return;

    var root = document.documentElement;
    var media = window.matchMedia('(prefers-color-scheme: dark)');

    function current() {
      var set = root.getAttribute('data-dt-theme');
      if (set === 'dark' || set === 'light') return set;
      return media.matches ? 'dark' : 'light';
    }

    // The label says what pressing DOES, not what the state is. Read from
    // data attributes so the strings stay in locales/ and get translated
    // with everything else.
    function label() {
      var next = current() === 'dark' ? 'light' : 'dark';
      var text = button.getAttribute('data-label-' + next) || '';
      button.setAttribute('aria-label', text);
      button.setAttribute('title', text);
      var sr = button.querySelector('[data-dt-theme-label]');
      if (sr) sr.textContent = text;
    }

    button.addEventListener('click', function () {
      var next = current() === 'dark' ? 'light' : 'dark';
      root.setAttribute('data-dt-theme', next);
      try { localStorage.setItem('dartec.theme', next); } catch (e) {}
      label();
    });

    // Someone on 'system' whose OS flips while the tab is open: the CSS
    // follows on its own, but the button's label would still promise the
    // wrong direction.
    var onChange = function () {
      if (!root.getAttribute('data-dt-theme')) label();
    };
    if (media.addEventListener) media.addEventListener('change', onChange);
    else if (media.addListener) media.addListener(onChange);

    label();
  }

  /* The collection sort.
   *
   * The sort is a plain GET form whose submit button only exists inside
   * <noscript>, so with scripts on, choosing a sort has to submit it from
   * here or nothing happens at all. Submitting the form, rather than building
   * a URL, keeps the hidden inputs carrying the active filters: a sort never
   * throws away what the customer filtered.
   *
   * Only a committed choice navigates. A closed select moved with the arrow
   * keys (or type-ahead) fires 'change' on every step in Chrome and Firefox
   * on Windows and Linux; submitting on each would take a keyboard user off
   * the page before they reached the option they wanted. A change caused by
   * one of those keys is held, and goes through on Enter, or when focus
   * leaves the select on a different option than it arrived on. A pick from
   * the open list, by mouse, touch or keyboard, arrives with no stepping key
   * in front of it and goes straight away.
   *
   * Delegated from the document, so it binds once and keeps working when the
   * theme editor re-renders the collection section in place. */
  function sort() {
    var SELECT = 'form.dartec-collection__sort select[name="sort_by"]';
    var STEP = /^(Arrow(Up|Down|Left|Right)|Home|End|Page(Up|Down)|.)$/;
    var stepping = false;

    function target(e) {
      var el = e.target;
      return el && el.matches && el.matches(SELECT) ? el : null;
    }

    // The value the select had when focus arrived: the one the page is
    // currently sorted by, unless the browser restored a different one.
    function submit(select) {
      if (select.value === select.getAttribute('data-dt-sort-from')) return;
      select.setAttribute('data-dt-sort-from', select.value);
      var form = select.form;
      if (form.requestSubmit) form.requestSubmit();
      else form.submit();
    }

    document.addEventListener('focusin', function (e) {
      var select = target(e);
      if (select) select.setAttribute('data-dt-sort-from', select.value);
    });

    document.addEventListener('keydown', function (e) {
      var select = target(e);
      if (!select || e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.key === 'Enter') {
        submit(select);
        return;
      }
      if (!STEP.test(e.key)) return;
      // 'change' fires during this key's default action, in the same task,
      // so the flag only has to outlive the current one.
      stepping = true;
      setTimeout(function () { stepping = false; }, 0);
    });

    document.addEventListener('change', function (e) {
      var select = target(e);
      if (select && !stepping) submit(select);
    });

    document.addEventListener('focusout', function (e) {
      var select = target(e);
      if (select) submit(select);
    });
  }

  function start() {
    drawer();
    theme();
    sort();
    pending();
  }

  /* Presses that lead to a slow page hold until it arrives.
   *
   * Add to cart is a plain form post to /cart/add, and the cart page is the
   * answer. Between the press and that page there is nothing, which on a
   * phone reads as a tap that missed and invites a second one. The button
   * holds its pressed state (dartec.css .is-pending), and a second submit
   * while it is pending is dropped rather than adding the item twice. Not
   * disabled: the browser builds the form data after this handler runs, and
   * a disabled submitter would drop name="add" from it.
   *
   * A product card or a filter chip tapped on a phone gets the same hold,
   * because on touch those two press only on the tap (see the chips in
   * dartec.css). Only a plain tap or click that opens in this tab: a
   * modified click opens a new tab and this page stays where it is, and a
   * keyboard Enter (detail 0) gets no motion.
   *
   * Every hold lets go after eight seconds. If the page never goes (the
   * visitor pressed Stop, the network dropped), a button stuck pressed and
   * refusing every later submit would be worse than a second add. */
  function pending() {
    var HOLD_MS = 8000;
    var held = [];

    function release(el) {
      el.classList.remove('is-pending');
      var form = el.closest('form');
      if (form) form.removeAttribute('aria-busy');
    }

    function hold(el) {
      el.classList.add('is-pending');
      held.push(el);
      setTimeout(function () { release(el); }, HOLD_MS);
    }

    Array.prototype.forEach.call(document.querySelectorAll('.dt-product__form'), function (form) {
      form.addEventListener('submit', function (e) {
        var btn = form.querySelector('[type="submit"]');
        if (!btn) return;
        if (btn.classList.contains('is-pending')) { e.preventDefault(); return; }
        hold(btn);
        form.setAttribute('aria-busy', 'true');
      });
    });

    document.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.detail === 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var link = e.target.closest('a.dartec__card, a.dartec__chip');
      if (!link || (link.target && link.target !== '_self')) return;
      hold(link);
    });

    // Back from the next page, Safari and Chrome restore this one as it was
    // left, held press and all.
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      held.forEach(release);
      held = [];
    });
  }

  // iOS Safari only applies :active, which carries the press feedback on
  // buttons and chips (dartec.css), on a page that listens for touches.
  // Passive, so it never delays a scroll.
  document.addEventListener('touchstart', function () {}, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
